import { ColumnDef } from '@tanstack/react-table'
import { compact } from 'lodash'
import MetricCell from 'app/components/table/MetricCell'
import { AttributionRowType } from 'app/types/attribution'

export const defaultAttributionColumns = (
  tableFilter:
    | `Source`
    | `Medium`
    | `Source / Medium`
    | `Campaign`
    | `Ad Campaign`
    | `Ad Platform`
    | `Channel`
    | `Reported Attribution`,
  showSpend: boolean,
  isSmc: boolean,
  onClickRow: (bucket: string, title?: string) => void,
): ColumnDef<AttributionRowType, any>[] => {
  let columns = compact([
    {
      enableSorting: true,
      accessorFn: (row) => row.bucket,
      header: `Name`,
      id: `Name`,
      cell: (props) => (
        <div className="flex items-center">
          {tableFilter === `Ad Platform` && (
            <>
              {(/google|facebook/.test(props.row.original.bucket.toLowerCase()) ||
                props.row.original.bucket === `Local Service Ads`) && (
                <img
                  src={
                    props.row.original.bucket.toLowerCase().includes(`google`) ||
                    props.row.original.bucket === `Local Service Ads`
                      ? `/public/google_ads.png`
                      : `/public/meta_icon.png`
                  }
                  alt={`${props.row.original.bucket} icon`}
                  className="mr-2 inline h-auto w-4"
                />
              )}
            </>
          )}
          <span className="font-bold capitalize">{props.row.original.bucket}</span>
        </div>
      ),
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: true,
        onClick: (row) =>
          tableFilter === `Ad Campaign` ? onClickRow(row.adCampaignId, row.bucket) : onClickRow(row.bucket),
      },
      enableHiding: false,
    },
    tableFilter === `Source / Medium` && {
      cell: function Cell({ row }) {
        return <>{row.original.channel}</>
      },
      header: `Channel`,
      id: `Channel`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
      },
    },
    {
      accessorKey: `leads`,
      cell: (props) => <MetricCell value={props.row.original.leads} />,
      accessorFn: (row) => row.leads,
      header: `Leads`,
      id: `Leads`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Leads`,
          message: `All contacts that have a became a lead date within the date range. This includes both qualified and disqualified leads.`,
        },
      },
    },
    {
      cell: (props) => <MetricCell value={props.row.original.mqls} />,
      accessorFn: (row) => row.mqls,
      header: `Qualified Leads`,
      id: `Qualified Leads`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Qualified Leads`,
          message: `All contacts with a became a lead date within the date range who does not have a disqualifying closed lost reason.`,
        },
      },
    },
    {
      cell: (props) => <MetricCell value={props.row.original.appointments} />,
      accessorFn: (row) => row.appointments,
      header: `Appointments`,
      id: `Appointments`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Appointments`,
          message: `All contacts who have a became a lead within the date range selected and have scheduled an appointment at any point. However this does not mean they have completed the appointment.`,
        },
      },
    },
    {
      cell: (props) => <MetricCell value={props.row.original.patients} />,
      accessorFn: (row) => row.patients,
      header: `Patients`,
      id: `Patients`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Patients`,
          message: `All contacts who have have a became a lead date within the date range and have completed a first appointment.`,
        },
      },
    },
    {
      cell: (props) => <MetricCell value={props.row.original.production} type="dollars" />,
      accessorFn: (row) => row.production,
      header: `Production`,
      id: `Production`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
      },
    },
    {
      cell: (props) => <MetricCell value={props.row.original.scheduledProduction} type="dollars" />,
      accessorFn: (row) => row.scheduledProduction,
      header: `Scheduled Production`,
      id: `Scheduled Production`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
      },
    },
    ...(showSpend
      ? ([
          {
            cell: (props) => {
              let spend = props.row.original.totalSpend
              if (tableFilter === `Ad Platform`) {
                if (props.row.original.bucket.toLowerCase().includes(`google`)) {
                  spend = props.row.original.googleSpend ?? 0
                } else if (props.row.original.bucket.toLowerCase().includes(`facebook`)) {
                  spend = props.row.original.facebookSpend ?? 0
                }
              }
              return <MetricCell value={spend} type="dollars" />
            },
            accessorFn: (row) => row.totalSpend,
            header: `Spend`,
            id: `Spend`,
            meta: {
              group: `Financial`,
              defaultVisible: true,
              infoTooltip: {
                title: `Spend`,
                message:
                  tableFilter === `Ad Campaign`
                    ? `Total Facebook spend + Google spend within the date range.`
                    : `Total Ad Platform spend within the date range.`,
              },
            },
          },
          ...(!isSmc
            ? ([
                {
                  accessorKey: `ROI`,
                  cell: (props) => <MetricCell value={props.row.original.ROI} type="percentage" />,
                  accessorFn: (row) => row.ROI,
                  header: `ROI`,
                  id: `ROI`,
                  meta: {
                    group: `Financial`,
                    defaultVisible: true,
                    infoTooltip: {
                      title: `ROI`,
                      message: `Return on investment: net production divided by spend.`,
                    },
                  },
                },
              ] as ColumnDef<AttributionRowType, any>[])
            : []),
        ] as ColumnDef<AttributionRowType, any>[])
      : []),
  ])

  return columns
}
